import React, { useRef, useEffect, useState, useCallback, useMemo } from "react";
import { Dialog, DialogContent } from "./ui/dialog";
import { Gift, Loader2, Sparkles, RefreshCw, Coins, Gamepad2, XCircle } from "lucide-react";
import confetti from "canvas-confetti";
import { api } from "../lib/api";

// Default (standard) wheel — Pep win / signup / home 10-coin spin.
const DEFAULT_SEGMENTS = [
  { id: "zero", short: "No win", label: "No win", color: "#2c3a35", weight: 50 },
  { id: "cash_5", short: "£5", label: "£5", color: "#f3efe3", weight: 22, dark: true },
  { id: "cash_20", short: "£20", label: "£20", color: "#6366F1", weight: 12 },
  { id: "tickets", short: "Tickets", label: "Matchday tickets", color: "#38BDF8", weight: 7, dark: true },
  { id: "retro_top", short: "Retro shirt", label: "Retro football shirt", color: "#10B981", weight: 4, dark: true },
  { id: "cash_30", short: "£30", label: "£30", color: "#EF4444", weight: 4.94 },
  { id: "cash_6000", short: "£6,000", label: "£6,000", color: "#F59E0B", weight: 0.05, dark: true },
  { id: "cash_10000", short: "£10,000", label: "£10,000", color: "#ffd700", weight: 0.01, dark: true, jackpot: true },
];

const TAU = Math.PI * 2;
const SIZE = 300;
const R = SIZE / 2 - 6;
const DISPLAY_SLICES = 20; // wheel is drawn with ~20 slices for a proper "spinny" look
const LIGHT_BG = new Set(["#f3efe3", "#e3b341", "#f2c230"]);

// --- sound (WebAudio, no assets) ---
let _actx;
const _ctx = () => {
  if (!_actx) {
    const AC = window.AudioContext || window.webkitAudioContext;
    if (AC) _actx = new AC();
  }
  return _actx;
};
const blip = (freq = 720, dur = 0.03, gain = 0.04) => {
  const ctx = _ctx();
  if (!ctx) return;
  try {
    if (ctx.state === "suspended") ctx.resume();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = "square";
    o.frequency.value = freq;
    g.gain.setValueAtTime(gain, ctx.currentTime);
    g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + dur);
    o.connect(g);
    g.connect(ctx.destination);
    o.start();
    o.stop(ctx.currentTime + dur);
  } catch (e) { /* no-op */ }
};
const winChime = () => {
  blip(660, 0.14, 0.05);
  setTimeout(() => blip(880, 0.16, 0.05), 110);
  setTimeout(() => blip(1174, 0.22, 0.045), 240);
};
const CONFETTI_COLORS = ["#10B981", "#10B981", "#ffffff", "#e3b341", "#1f5c45", "#7a1f33"];
const fireConfetti = (big = false) => {
  try {
    if (big) {
      confetti({ particleCount: 170, spread: 95, startVelocity: 48, origin: { y: 0.5 }, colors: CONFETTI_COLORS, zIndex: 100000 });
      setTimeout(() => confetti({ particleCount: 90, angle: 60, spread: 75, origin: { x: 0, y: 0.65 }, colors: CONFETTI_COLORS, zIndex: 100000 }), 160);
      setTimeout(() => confetti({ particleCount: 90, angle: 120, spread: 75, origin: { x: 1, y: 0.65 }, colors: CONFETTI_COLORS, zIndex: 100000 }), 160);
    } else {
      confetti({ particleCount: 90, spread: 70, origin: { y: 0.5 }, colors: CONFETTI_COLORS, zIndex: 100000 });
    }
  } catch (e) { /* no-op */ }
};

// Spread the prizes across N equal slices, weight-proportional and interleaved
// so the same prize isn't clustered — looks like a busy real prize wheel.
const buildOrder = (segments, N) => {
  const total = segments.reduce((a, s) => a + s.weight, 0);
  const raw = segments.map((s) => (s.weight / total) * N);
  const counts = raw.map((x) => Math.max(1, Math.floor(x)));
  let diff = N - counts.reduce((a, b) => a + b, 0);
  const fr = raw.map((x, i) => ({ i, f: x - Math.floor(x) })).sort((a, b) => b.f - a.f);
  let j = 0;
  while (diff > 0) { counts[fr[j % fr.length].i]++; diff--; j++; }
  const bySize = counts.map((c, i) => ({ i, c })).sort((a, b) => b.c - a.c);
  j = 0;
  while (diff < 0) { const idx = bySize[j % bySize.length].i; if (counts[idx] > 1) { counts[idx]--; diff++; } j++; }
  const rem = counts.slice();
  const order = [];
  let last = -1;
  for (let k = 0; k < N; k++) {
    let best = -1;
    for (let i = 0; i < segments.length; i++) { if (rem[i] <= 0) continue; if (best < 0 || rem[i] > rem[best]) best = i; }
    if (best === last) {
      let alt = -1;
      for (let i = 0; i < segments.length; i++) { if (rem[i] <= 0 || i === last) continue; if (alt < 0 || rem[i] > rem[alt]) alt = i; }
      if (alt >= 0) best = alt;
    }
    order.push(best);
    rem[best]--;
    last = best;
  }
  return order;
};

const wheelToken = (short) => short.replace(" Amazon", "");

const PepSpinWheel = ({
  open,
  onOpenChange,
  onCoins,
  onSpun,
  inline = false,
  repeatable = false,
  cost = 10,
  freeSpin = true,
  coins = null,
  eyebrow = "You beat Pep",
  title = "Spin the wheel",
  subtitle = "One spin. Up to £10,000 to win.",
  segments = DEFAULT_SEGMENTS,
  spinApi = api.pepSpin,
  onLockedSpin,
}) => {
  const canvasRef = useRef(null);
  const pointerRef = useRef(null);
  const rotRef = useRef(-Math.PI / 2);
  const rafRef = useRef(null);
  const [spinning, setSpinning] = useState(false);
  const [prize, setPrize] = useState(null);
  const [error, setError] = useState("");
  const [freeLeft, setFreeLeft] = useState(freeSpin);
  const [bal, setBal] = useState(coins);

  useEffect(() => { setFreeLeft(freeSpin); }, [freeSpin]);
  useEffect(() => { setBal(coins); }, [coins]);

  const slices = useMemo(() => {
    const N = Math.max(DISPLAY_SLICES, segments.length);
    const order = buildOrder(segments, N);
    const span = TAU / N;
    return order.map((segIndex, i) => {
      const s = segments[segIndex];
      const start = i * span;
      return { segIndex, color: s.color, short: s.short, dark: s.dark, jackpot: s.jackpot, start, end: start + span, mid: start + span / 2 };
    });
  }, [segments]);

  const sliceUnderPointer = useCallback((rot) => {
    const n = slices.length;
    const a = (((-Math.PI / 2 - rot) % TAU) + TAU) % TAU;
    return Math.floor(a / (TAU / n));
  }, [slices.length]);

  const flick = useCallback(() => {
    const el = pointerRef.current;
    if (!el) return;
    el.style.transform = "translateX(-50%) rotate(15deg)";
    clearTimeout(el._t);
    el._t = setTimeout(() => { el.style.transform = "translateX(-50%) rotate(0deg)"; }, 70);
  }, []);

  const draw = useCallback((rot) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const cx = SIZE / 2;
    const cy = SIZE / 2;
    ctx.clearRect(0, 0, SIZE, SIZE);
    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(rot);
    [...slices].sort((a, b) => (a.jackpot ? 1 : 0) - (b.jackpot ? 1 : 0)).forEach((seg) => {
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.arc(0, 0, R, seg.start, seg.end);
      ctx.closePath();
      if (seg.jackpot) {
        const ph = (performance.now() % 1600) / 1600;
        const g = ctx.createLinearGradient(R * Math.cos(seg.start), R * Math.sin(seg.start), R * Math.cos(seg.end), R * Math.sin(seg.end));
        g.addColorStop(0, "#b8860b");
        g.addColorStop(Math.max(0.01, ph - 0.25), "#ffd700");
        g.addColorStop(ph, "#fffbe6");
        g.addColorStop(Math.min(0.99, ph + 0.25), "#ffd700");
        g.addColorStop(1, "#b8860b");
        ctx.save();
        ctx.shadowColor = "rgba(255,215,0,0.95)";
        ctx.shadowBlur = 14 + 8 * Math.sin(ph * TAU);
        ctx.fillStyle = g;
        ctx.fill();
        ctx.restore();
        ctx.lineWidth = 2.5;
        ctx.strokeStyle = "#fff3b0";
        ctx.stroke();
      } else {
        ctx.fillStyle = seg.color;
        ctx.fill();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = "rgba(0,0,0,0.3)";
        ctx.stroke();
      }
      ctx.save();
      ctx.rotate(seg.mid);
      ctx.textAlign = "right";
      ctx.textBaseline = "middle";
      ctx.fillStyle = seg.dark || LIGHT_BG.has(seg.color) ? "#14241c" : "#ffffff";
      const tok = wheelToken(seg.short);
      ctx.font = `700 ${tok.length > 5 ? 8.5 : 10}px 'Segoe UI', system-ui, sans-serif`;
      ctx.fillText(tok, R - 8, 0);
      ctx.restore();
    });
    ctx.restore();
    ctx.beginPath();
    ctx.arc(cx, cy, 24, 0, TAU);
    ctx.fillStyle = "#0F172A";
    ctx.fill();
    ctx.lineWidth = 3;
    ctx.strokeStyle = "#10B981";
    ctx.stroke();
  }, [slices]);

  useEffect(() => {
    if (inline || open) {
      if (!inline && open) { setPrize(null); setError(""); setSpinning(false); }
      const id = requestAnimationFrame(() => draw(rotRef.current));
      return () => cancelAnimationFrame(id);
    }
  }, [inline, open, draw]);

  useEffect(() => () => rafRef.current && cancelAnimationFrame(rafRef.current), []);

  // Keep the jackpot slice shimmering while the wheel is idle.
  useEffect(() => {
    if (!(inline || open) || spinning || !slices.some((s) => s.jackpot)) return;
    let id;
    let last = 0;
    const loop = (now) => {
      if (now - last > 33) { draw(rotRef.current); last = now; }
      id = requestAnimationFrame(loop);
    };
    id = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(id);
  }, [inline, open, spinning, draw, slices]);

  const animateTo = (segIndex, onDone) => {
    const candidates = slices.map((s, i) => i).filter((i) => slices[i].segIndex === segIndex);
    const sliceIdx = candidates[Math.floor(Math.random() * candidates.length)];
    const startRot = rotRef.current;
    const base = -Math.PI / 2 - slices[sliceIdx].mid;
    const target = startRot + ((((base - startRot) % TAU) + TAU) % TAU) + TAU * 7; // 7 full turns
    const delta = target - startRot;
    const duration = 5800;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 4.5); // strong, drawn-out slow finish
    let lastSeg = sliceUnderPointer(startRot);
    const step = (now) => {
      const t = Math.min(1, (now - t0) / duration);
      rotRef.current = startRot + delta * ease(t);
      draw(rotRef.current);
      const seg = sliceUnderPointer(rotRef.current);
      if (seg !== lastSeg) { blip(760, 0.02, 0.035); flick(); lastSeg = seg; }
      if (t < 1) rafRef.current = requestAnimationFrame(step);
      else { rotRef.current = target % TAU; onDone(); }
    };
    rafRef.current = requestAnimationFrame(step);
  };

  const canAfford = freeLeft || bal == null || bal >= cost;

  const spin = async () => {
    if (spinning) return;
    if (onLockedSpin) { onLockedSpin(); return; }
    if (!canAfford) { setError(`You need £${cost} to spin`); return; }
    setSpinning(true);
    setError("");
    setPrize(null);
    try {
      const res = await spinApi();
      animateTo(res.index, () => {
        setPrize(res.prize);
        setSpinning(false);
        if (res.free) setFreeLeft(false);
        if (res.respin) setFreeLeft(true);
        setBal(res.coins);
        onCoins && onCoins(res.coins);
        onSpun && onSpun();
        if (res.prize.kind !== "none") {
          winChime();
          const big = res.prize.kind === "claim" || res.prize.kind === "pep" || (res.prize.kind === "coins" && (res.prize.coins || 0) >= 500);
          fireConfetti(big);
        }
      });
    } catch (e) {
      setSpinning(false);
      setError(e?.response?.data?.detail || "Spin unavailable");
    }
  };

  const spinLabel = onLockedSpin ? "Sign up to spin" : freeLeft ? "Spin — free!" : `Spin for £${cost}`;
  const isNone = prize && prize.kind === "none";
  const ResultIcon = prize
    ? (prize.kind === "respin" ? RefreshCw : prize.kind === "coins" ? Coins : prize.kind === "pep" ? Gamepad2 : prize.kind === "none" ? XCircle : Gift)
    : Gift;
  const accent = isNone ? "#9aa4b2" : "#10B981";

  const content = (
    <>
      <div className="text-center">
        <div className="inline-flex items-center gap-2 text-[11px] font-semibold label-eyebrow text-[#10B981]">
          <Sparkles className="w-4 h-4" /> {eyebrow}
        </div>
        <h2 className="font-display font-extrabold text-2xl mt-1">{title}</h2>
        {subtitle && <p className="text-muted-38 text-xs mt-1">{subtitle}</p>}
      </div>

      <div className="relative mx-auto my-4" style={{ width: SIZE, maxWidth: "100%" }}>
        <div
          ref={pointerRef}
          className="absolute left-1/2 -top-1 z-10"
          style={{
            width: 0,
            height: 0,
            borderLeft: "11px solid transparent",
            borderRight: "11px solid transparent",
            borderTop: "20px solid #10B981",
            filter: "drop-shadow(0 2px 2px rgba(0,0,0,0.5))",
            transform: "translateX(-50%)",
            transformOrigin: "50% 100%",
            transition: "transform 70ms ease-out",
          }}
        />
        <canvas ref={canvasRef} width={SIZE} height={SIZE} className="w-full h-auto rounded-full" style={{ maxWidth: SIZE }} />
      </div>

      {prize && (
        <div data-testid="pep-spin-result" className="text-center mb-3">
          <div className="inline-flex items-center gap-2 rounded-xl px-4 py-2.5" style={{ background: `${accent}1f`, border: `1px solid ${accent}4d` }}>
            <ResultIcon className="w-5 h-5" style={{ color: accent }} />
            <span className="font-display font-bold" style={{ color: accent }}>
              {isNone ? "No win this time" : prize.kind === "respin" ? "Re-spin — free go!" : `You won ${prize.label}!`}
            </span>
          </div>
          <p className="text-muted-38 text-xs mt-3">
            {isNone
              ? "Unlucky — spin again for another shot."
              : prize.kind === "respin"
              ? "Nice — spin again on the house."
              : prize.kind === "coins"
              ? "Added to your virtual balance."
              : prize.kind === "pep"
              ? "Added to your account — play Pep for free."
              : /^£[\d,]+$/.test(prize.label)
              ? "That's a real £ prize. Claim it from Your Prizes (trophy icon up top) and we'll be in touch."
              : "Claim it anytime from Your Prizes (trophy icon up top)."}
          </p>
        </div>
      )}

      {(!prize || repeatable || freeLeft) && (
        <button
          data-testid="pep-spin-btn"
          onClick={spin}
          disabled={spinning || (!freeLeft && !canAfford)}
          className="btn-green w-full rounded-xl py-3.5 font-semibold flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {spinning ? (
            <><Loader2 className="w-4 h-4 animate-spin" /> Spinning…</>
          ) : prize ? (
            freeLeft ? "Spin again — free!" : `Spin again — £${cost}`
          ) : (
            spinLabel
          )}
        </button>
      )}

      {!prize && !canAfford && (
        <p className="text-muted-38 text-[11px] text-center mt-2">Not enough balance — win some first.</p>
      )}
      {error && <p data-testid="pep-spin-error" className="text-red-400 text-xs text-center mt-2">{error}</p>}

      {prize && !repeatable && onOpenChange && (
        <button
          data-testid="pep-spin-close"
          onClick={() => onOpenChange(false)}
          className="btn-ghost w-full rounded-xl py-2.5 text-sm font-medium text-slate-300 mt-2"
        >
          Done
        </button>
      )}
    </>
  );

  if (inline) {
    return (
      <div data-testid="home-spin-wheel" className="card-38 p-5 hover:translate-y-0">
        {content}
      </div>
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !spinning && onOpenChange(v)}>
      <DialogContent data-testid="pep-spin-wheel" className="w-[calc(100%-2rem)] bg-[#1E293B] border border-white/10 text-white max-w-sm p-5">
        {content}
      </DialogContent>
    </Dialog>
  );
};

export default PepSpinWheel;
